import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';

// Material UI
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { LoadingButton } from '@mui/lab';
import Drawer from '@mui/material/Drawer';
import LinearProgress from '@mui/material/LinearProgress';

// Icons
import FolderIcon from '@mui/icons-material/Folder';
import { FileIcon } from './FileIcon';
import EmlViewer from './EmlViewer';
import MsgViewer from './MsgViewer';
import PdfViewer from './PdfViewer';
const DocumentBrowser = ({ handleFolderClick, entriesPage, accessKey, hideHeader }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const downloadMutation = useMutation({
    mutationFn: async (requestedDocument) => {
      if (fileBlob) {
        return fileBlob;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/get-document?documentId=${requestedDocument.id}`, {
        headers: {
          'X-Signature': accessKey
        },
      });
      return await response.blob();
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", selectedFile.name);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  });

  const previewMutation = useMutation({
    mutationFn: async (requestedDocument) => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/get-document?documentId=${requestedDocument.id}`, {
        headers: {
          'X-Signature': accessKey
        },
      });
      
      const blob = await response.blob();
      setFileBlob(blob);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    }
  });

  const clearPreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setFileBlob(null);
    }
  };

  const handleClosePreview = () => {
    clearPreview();
    setDrawerOpen(false);
    setSelectedFile(null);
  };

  const canPreviewFile = (file) => {
    return (file?.mimeType?.startsWith('image/') && 
            !file?.mimeType?.includes('tiff') && 
            !file?.mimeType?.includes('tif')) || 
           file?.mimeType?.startsWith('application/pdf') ||
           file?.mimeType?.startsWith('text/') ||
           file?.mimeType === 'message/rfc822' ||
           file?.name?.toLowerCase().endsWith('.eml') ||
           file?.name?.toLowerCase().endsWith('.msg') ||
           file?.name?.toLowerCase().endsWith('.txt') ||
           file?.name?.toLowerCase().endsWith('.htm') ||
           file?.name?.toLowerCase().endsWith('.html');
  };

  const handleItemClick = (item) => {
    if (item.isFolder) {
      handleFolderClick(item.name, item.id);
    } else if (item.isFile) {
      setSelectedFile(item);
      setDrawerOpen(true);
      
      clearPreview();
      previewMutation.mutate({
        id: item.id,
        name: item.name
      });
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'flex', px: 2 }}>
      <Box sx={{ 
        width: drawerOpen ? '40%' : '100%',
        transition: 'width 0.3s',
        height: '100%',
        maxHeight: hideHeader ? 'calc(100vh - 148px)' : 'calc(100vh - 348px)',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <List sx={{ flex: 1 }}>
          {entriesPage && entriesPage.length > 0 && entriesPage.map((item) => (
            <ListItemButton
              key={item.name}
              onClick={() => handleItemClick(item)}
              aria-label={`${item.isFolder ? "Open Folder" : "Preview File"} ${item.name}`}
              sx={{ 
                cursor: 'pointer', 
                display: 'flex', 
                justifyContent: 'space-between',
                bgcolor: drawerOpen && selectedFile?.id === item.id ? 'action.selected' : 'inherit',
                '&:hover': {
                  bgcolor: drawerOpen && selectedFile?.id === item.id 
                    ? 'action.selected' 
                    : 'action.hover'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon>
                  {item.isFolder ? <FolderIcon color="primary" /> : FileIcon(item.mimeType)}
                </ListItemIcon>
                <ListItemText primary={item.name} />
              </Box>
              {item.isFile &&
              <ListItemText 
                primary={dayjs(item.createdAt).format('DD/MM/YYYY HH:mm A')}
                secondary={(item.lotNumber && item.lotNumber !== '\n  ') ? "Lot:" + item.lotNumber : ""}
                sx={{ textAlign: 'right', flexShrink: 0 }}
              />
              }
            </ListItemButton>
          ))}
        </List>
      </Box>

      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: hideHeader ? 'calc(100vh - 148px)' : 'calc(100vh - 348px)',
        overflow: selectedFile?.mimeType?.startsWith('application/pdf') ? 'hidden' : 'auto',
        width: drawerOpen ? '60%' : '0%',
        position: 'sticky',
        top: 350,
        zIndex: 1
      }}>
      {drawerOpen && (
        <>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1,
            borderBottom: 1, 
            borderColor: 'divider',
            pb: 2,
            mb: 2,
            ml: 3
          }}>
            {selectedFile && (
              <Typography variant="h6" >
                {selectedFile.name}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                onClick={handleClosePreview}
              >
                Close
              </Button>
              <LoadingButton
                variant="contained"
                onClick={() => downloadMutation.mutate({
                  id: selectedFile.id,
                  name: selectedFile.name
                })}
                loading={downloadMutation.isPending}
              >
                Download
              </LoadingButton>
            </Box>
          </Box>

          {previewMutation.isPending && (
            <Box sx={{ width: '100%', position: 'relative' }}>
              <LinearProgress />
            </Box>
          )}
          
          <Box sx={{ 
            // height: 'calc(100vh - 140px)',
            opacity: previewMutation.isPending ? 0.5 : 1,
            transition: 'opacity 0.2s'
          }}>
            {!canPreviewFile(selectedFile) ? (
              <Typography variant="body1" sx={{ p: 2 }}>
                This file type is not supported for preview yet.
              </Typography>
            ) : previewUrl ? (
              selectedFile?.mimeType?.startsWith('image/') && 
              !selectedFile?.mimeType?.includes('tiff') && 
              !selectedFile?.mimeType?.includes('tif') ? (
                <img src={previewUrl} alt={selectedFile.name} style={{ maxWidth: '100%', maxHeight: '100%' }} />
              ) : selectedFile?.mimeType?.startsWith('application/pdf') ? (
                <PdfViewer body={previewUrl} hideHeader={hideHeader}/>
              ) : selectedFile?.mimeType === 'message/rfc822' || selectedFile?.name?.toLowerCase().endsWith('.eml') ? (
                <EmlViewer blob={fileBlob} />
              ) : selectedFile?.name?.toLowerCase().endsWith('.msg') ? (
                <MsgViewer blob={fileBlob} />
              ) : (selectedFile?.mimeType?.startsWith('text/') || 
                  selectedFile?.name?.toLowerCase().endsWith('.txt') ||
                  selectedFile?.name?.toLowerCase().endsWith('.htm') ||
                  selectedFile?.name?.toLowerCase().endsWith('.html')) ? (
                <PdfViewer body={previewUrl} />
              ) : (
                <Typography variant="body1" sx={{ p: 2 }}>
                  This file type is not supported for preview yet.
                </Typography>
              )
            ) : null}
          </Box>
          
          </>
        )}
      </Box>
      
    </Box>
  );
};

export default DocumentBrowser;